import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Menu, X, CalendarClock } from "lucide-react";
import { Logo } from "@/components/Logo";

const LINKS = [
  { id: "guide", label: "Buyer's Guide" },
  { id: "listings", label: "Listings", page: "/listings" },
  { id: "blog", label: "Journal", page: "/blog" },
  { id: "checklist", label: "Checklist" },
  { id: "services", label: "Services" },
  { id: "contact", label: "Contact" },
];

export const scrollTo = (id) => {
  document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
};

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const isHome = useLocation().pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (l) => {
    setOpen(false);
    if (l.page) {
      window.location.href = l.page;
    } else if (isHome) {
      scrollTo(l.id);
    } else {
      window.location.href = `/#${l.id}`;
    }
  };

  const book = () => {
    setOpen(false);
    if (isHome) scrollTo("booking");
    else window.location.href = "/#booking";
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled || !isHome ? "glass border-b border-white/5" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-24 flex items-center justify-between">
        <Logo />
        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l)}
              className="text-sm text-zinc-300 hover:text-gold-light tracking-wide transition-colors"
            >
              {l.label}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <button
            data-testid="nav-book-consultation-btn"
            onClick={book}
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-semibold text-ink hover:bg-gold-hover transition-colors"
          >
            <CalendarClock size={16} />
            Book Consultation
          </button>
          <button
            data-testid="nav-mobile-menu-btn"
            onClick={() => setOpen(!open)}
            className="lg:hidden text-zinc-200 p-2"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden glass border-t border-white/5 px-6 py-4 flex flex-col gap-1" data-testid="nav-mobile-menu">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-mobile-link-${l.id}`}
              onClick={() => go(l)}
              className="text-left py-2.5 text-zinc-200 hover:text-gold-light transition-colors"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="nav-mobile-book-btn"
            onClick={book}
            className="mt-2 rounded-full bg-gold px-5 py-3 text-sm font-semibold text-ink"
          >
            Book Consultation
          </button>
        </div>
      )}
    </header>
  );
};
